import { useFormattedDate, useLanguage, useTimezone } from "@/hooks/usePreferences"

/**
 * Hook that provides localized date formatting based on user preferences
 * This is a reusable utility for formatting dates across the application
 */
export function useLocalizedDateFormat() {
  const { language } = useLanguage()
  const { timezone } = useTimezone()
  const { formatDateTime, formatTime } = useFormattedDate()

  // Get date format options based on language
  const getDateFormatOptions = (
    type: "short" | "medium" | "long" | "full" = "medium",
  ): Intl.DateTimeFormatOptions => {
    const baseOptions: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
    }

    switch (type) {
      case "short":
        return {
          ...baseOptions,
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
        }
      case "medium":
        return {
          ...baseOptions,
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }
      case "long":
        return {
          ...baseOptions,
          day: "numeric",
          month: "long",
          year: "numeric",
        }
      case "full":
        return {
          ...baseOptions,
          day: "numeric",
          month: "long",
          year: "numeric",
          weekday: "long",
        }
      default:
        return baseOptions
    }
  }

  // Get datetime format options based on language
  const getDateTimeFormatOptions = (
    type: "short" | "medium" | "long" = "medium",
    includeSeconds: boolean = false,
  ): Intl.DateTimeFormatOptions => {
    const dateOptions = getDateFormatOptions(type)

    // Language-specific adjustments
    switch (language) {
      case "vi":
        return {
          ...dateOptions,
          hour: "2-digit",
          minute: "2-digit",
          ...(includeSeconds && { second: "2-digit" }),
          hour12: false,
        }
      case "fr":
      case "es":
        return {
          ...dateOptions,
          hour: "2-digit",
          minute: "2-digit",
          ...(includeSeconds && { second: "2-digit" }),
        }
      default:
        return {
          ...dateOptions,
          hour: "numeric",
          minute: "2-digit",
          ...(includeSeconds && { second: "2-digit" }),
        }
    }
  }

  // Get time format options
  const getTimeFormatOptions = (includeSeconds: boolean = false): Intl.DateTimeFormatOptions => {
    return {
      timeZone: timezone,
      hour: language === "vi" ? "2-digit" : "numeric",
      minute: "2-digit",
      ...(includeSeconds && { second: "2-digit" }),
      hour12: language !== "vi",
    }
  }

  // Predefined format functions for common use cases
  const formatCreatedAt = (dateString: string | null | undefined) => {
    if (!dateString) return "Never"
    return formatDateTime(dateString, getDateTimeFormatOptions("short"))
  }

  const formatLastLogin = (dateString: string | null | undefined) => {
    if (!dateString) return "Never"
    return formatDateTime(dateString, getDateTimeFormatOptions("medium"))
  }

  const formatTableDate = (dateString: string) => {
    return formatDateTime(dateString, getDateFormatOptions("short"))
  }

  const formatTableDateTime = (dateString: string) => {
    return formatDateTime(dateString, getDateTimeFormatOptions("short"))
  }

  const formatFullDateTime = (dateString: string) => {
    return formatDateTime(dateString, getDateTimeFormatOptions("long", true))
  }

  // Relative time formatting (e.g., "2 hours ago", "2 horas atras")
  const formatRelativeTime = (dateString: string | null | undefined): string => {
    if (!dateString) return "Never"

    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffSeconds = Math.floor(diffMs / 1000)
    const diffMinutes = Math.floor(diffSeconds / 60)
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)
    const diffWeeks = Math.floor(diffDays / 7)
    const diffMonths = Math.floor(diffDays / 30)
    const diffYears = Math.floor(diffDays / 365)

    const rtf = new Intl.RelativeTimeFormat(language, { numeric: "auto" })

    if (Math.abs(diffSeconds) < 60) {
      return rtf.format(-diffSeconds, "second")
    } else if (Math.abs(diffMinutes) < 60) {
      return rtf.format(-diffMinutes, "minute")
    } else if (Math.abs(diffHours) < 24) {
      return rtf.format(-diffHours, "hour")
    } else if (Math.abs(diffDays) < 7) {
      return rtf.format(-diffDays, "day")
    } else if (Math.abs(diffWeeks) < 4) {
      return rtf.format(-diffWeeks, "week")
    } else if (Math.abs(diffMonths) < 12) {
      return rtf.format(-diffMonths, "month")
    } else {
      return rtf.format(-diffYears, "year")
    }
  }

  return {
    // Format options getters
    getDateFormatOptions,
    getDateTimeFormatOptions,
    getTimeFormatOptions,

    // Predefined formatters
    formatCreatedAt,
    formatLastLogin,
    formatTableDate,
    formatTableDateTime,
    formatFullDateTime,
    formatRelativeTime,

    // Base formatters from hook
    formatTime,
    formatDateTime,

    // Current preferences
    language,
    timezone,
  }
}

/**
 * Utility function to format date with preferences (non-hook version)
 * Useful for cases where hooks cannot be used (e.g., outside React components)
 */
export function formatDateWithPreferences(
  dateString: string | null | undefined,
  options: {
    type?: "short" | "medium" | "long" | "full"
    includeTime?: boolean
    includeSeconds?: boolean
    language?: string
    timezone?: string
  } = {},
): string {
  const {
    type = "medium",
    includeTime = true,
    includeSeconds = false,
    language = "en-AU",
    timezone = "Australia/Sydney",
  } = options

  // Handle null or undefined dates
  if (!dateString) {
    return "Never"
  }

  const date = new Date(dateString)

  const formatOptions: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
  }

  // Apply type-specific formatting
  switch (type) {
    case "short":
      formatOptions.day = "2-digit"
      formatOptions.month = "2-digit"
      formatOptions.year = "2-digit"
      break
    case "medium":
      formatOptions.day = "2-digit"
      formatOptions.month = "2-digit"
      formatOptions.year = "numeric"
      break
    case "long":
      formatOptions.day = "numeric"
      formatOptions.month = "long"
      formatOptions.year = "numeric"
      break
    case "full":
      formatOptions.day = "numeric"
      formatOptions.month = "long"
      formatOptions.year = "numeric"
      formatOptions.weekday = "long"
      break
  }

  // Add time if requested
  if (includeTime) {
    formatOptions.hour = language === "vi" ? "2-digit" : "numeric"
    formatOptions.minute = "2-digit"
    if (includeSeconds) {
      formatOptions.second = "2-digit"
    }
    formatOptions.hour12 = language !== "vi"
  }

  return date.toLocaleString(language, formatOptions)
}
